import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline';
import { ttsService } from '@/services/ttsService';
import { useVoiceSettingsStore } from '@/store/voiceSettingsStore';
import './VoiceControls.css';

// Custom Modern Icons
const SpeedIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const PitchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const VolumeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);

const MicrophoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const VoiceControls: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { selectedVoiceIndex, rate, pitch, volume, setVoice, setRate, setPitch, setVolume } =
    useVoiceSettingsStore();

  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = ttsService.getEnglishVoices();
      setVoices(availableVoices);
    };

    loadVoices();

    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsVoiceDropdownOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleTestVoice = () => {
    const voice = selectedVoiceIndex >= 0 ? voices[selectedVoiceIndex] : undefined;
    ttsService.speak('Hello! This is how I will sound.', {
      voice,
      rate,
      pitch,
      volume,
    });
  };

  const getSelectedVoiceName = () => {
    if (selectedVoiceIndex >= 0 && voices[selectedVoiceIndex]) {
      return voices[selectedVoiceIndex].name.split(' ')[0];
    }
    return 'Default';
  };

  const getFullVoiceName = () => {
    if (selectedVoiceIndex >= 0 && voices[selectedVoiceIndex]) {
      return voices[selectedVoiceIndex].name;
    }
    return 'Default Voice';
  };

  const handleVoiceSelect = (index: number) => {
    setVoice(index);
    setIsVoiceDropdownOpen(false);
  };

  return (
    <div className="voice-controls" ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="voice-controls-toggle"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <SpeakerWaveIcon className="w-5 h-5" />
        <span className="voice-label">{getSelectedVoiceName()}</span>
        <ChevronDownIcon className={`w-4 h-4 chevron ${isOpen ? 'open' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="voice-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {/* Voice Selection */}
            <div className="dropdown-section">
              <label className="dropdown-label">
                <MicrophoneIcon />
                Voice
              </label>
              <div className="custom-select-wrapper">
                <button
                  className="custom-select-trigger"
                  onClick={() => setIsVoiceDropdownOpen(!isVoiceDropdownOpen)}
                  type="button"
                >
                  <span className="selected-voice-name">{getFullVoiceName()}</span>
                  <ChevronDownIcon
                    className={`w-4 h-4 select-chevron ${isVoiceDropdownOpen ? 'open' : ''}`}
                  />
                </button>

                <AnimatePresence>
                  {isVoiceDropdownOpen && (
                    <motion.div
                      className="custom-select-options"
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                    >
                      <button
                        className={`custom-option ${selectedVoiceIndex === -1 ? 'selected' : ''}`}
                        onClick={() => handleVoiceSelect(-1)}
                        type="button"
                      >
                        <span>Default Voice</span>
                        {selectedVoiceIndex === -1 && (
                          <span className="check-mark">
                            <CheckIcon />
                          </span>
                        )}
                      </button>
                      {voices.map((voice, index) => (
                        <button
                          key={index}
                          className={`custom-option ${selectedVoiceIndex === index ? 'selected' : ''}`}
                          onClick={() => handleVoiceSelect(index)}
                          type="button"
                        >
                          <span>{voice.name}</span>
                          {selectedVoiceIndex === index && (
                            <span className="check-mark">
                              <CheckIcon />
                            </span>
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Speed Control */}
            <div className="dropdown-section">
              <label className="dropdown-label">
                <SpeedIcon />
                Speed: {rate.toFixed(1)}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="voice-slider"
              />
              <div className="slider-marks">
                <span>Slow</span>
                <span>Normal</span>
                <span>Fast</span>
              </div>
            </div>

            {/* Pitch Control */}
            <div className="dropdown-section">
              <label className="dropdown-label">
                <PitchIcon />
                Pitch: {pitch.toFixed(1)}
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={pitch}
                onChange={(e) => setPitch(Number(e.target.value))}
                className="voice-slider"
              />
              <div className="slider-marks">
                <span>Low</span>
                <span>Normal</span>
                <span>High</span>
              </div>
            </div>

            {/* Volume Control */}
            <div className="dropdown-section">
              <label className="dropdown-label">
                <VolumeIcon />
                Volume: {Math.round(volume * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="voice-slider"
              />
              <div className="slider-marks">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Test Button */}
            <motion.button
              onClick={handleTestVoice}
              className="test-voice-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SpeakerWaveIcon className="w-5 h-5" />
              Test Voice
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
